/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Feature } from 'src/luis/modules/features/types/Feature';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { HierarchicalEntity } from 'src/luis/projects/entities/types/HierarchicalEntity';
import { ListEntity } from 'src/luis/projects/entities/types/ListEntity';
import { RegexEntity } from 'src/luis/projects/entities/types/RegexEntity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { Sublist } from 'src/luis/projects/entities/types/Sublist';
import { ModelType } from 'src/luis/types/ModelType';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { FeatureStore } from 'src/luis/types/stores/features/FeatureStore';
import { PhraseListStore } from 'src/luis/types/stores/features/PhraseListStore';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';

/**
 * Uses `entityDenormalizer` to de-normalize the given entity stores to their equivalent entity objects.
 */
export const entitiesDenormalizer = (
    entityStores: EntityStore[],
    intentStores: IntentStore[],
    phraseListStores: PhraseListStore[]
): Entity[] => entityStores.map(entityDenormalizer(entityStores, intentStores, phraseListStores));

/**
 *  Denormalizes entity store into entity object based on the following:
 *  - Composite entity:
 *      - Map entity children IDs to entities object
 *      - If the child of the composite entity is an role entity
 *            it'll map to an role entity object with a new name roleParent:roleName
 * - List entity:
 *      - Map the entity store and sublists store to list entity object with sublists objects
 * - Remaining entity types:
 *      - Map the entity store to entity object.
 */
export const entityDenormalizer = (entityStores: EntityStore[], intentStores: IntentStore[], phraseListStores: PhraseListStore[]) => (
    entityStore: EntityStore
): Entity => {
    const simpleEntity = {
        id: entityStore.id,
        name: entityStore.name,
        typeId: entityStore.typeId,
        roles: entityStore.entityRoleStoreHouse.items.map(r => ({
            id: r.id,
            name: r.name
        })),
        labelsCount: entityStore.labelsCount
    };
    switch (entityStore.typeId) {
        case ModelType.Simple:
            return <SimpleEntity>{
                ...simpleEntity,
                children: (<SimpleEntityStore>entityStore).children.map(
                    child => <SimpleEntity>deNormalizeNode(<SimpleEntityStore>child, entityStores, intentStores, phraseListStores)
                ),
                features: deNormalizeFeatures(<SimpleEntityStore>entityStore, entityStores, intentStores, phraseListStores)
            };

        case ModelType.Hierarchical:
            return <HierarchicalEntity>{
                ...simpleEntity,
                children: (<HierarchicalEntityStore>entityStore).children.map(child => ({ ...child, roles: [] }))
            };
        case ModelType.Composite:
            // eslint-disable array-callback-return
            const demoralizedChildren: Entity[] = (<CompositeEntityStore>entityStore).children.map(childId => {
                for (const childEntityStore of entityStores) {
                    if (childEntityStore.id === childId) {
                        return entityDenormalizer(entityStores, intentStores, phraseListStores)(childEntityStore);
                    }
                    if (childEntityStore.typeId === ModelType.Hierarchical) {
                        const foundChild = (<HierarchicalEntityStore>childEntityStore).children.find(child => child.id === childId);
                        if (foundChild) {
                            return {
                                id: foundChild.id,
                                name: `${childEntityStore.name}::${foundChild.name}`,
                                roles: [],
                                typeId: ModelType.HierarchicalChild,
                                labelsCount: 0
                            };
                        }
                    } else {
                        const foundRoleChild = childEntityStore.entityRoleStoreHouse.findById(childId);
                        if (foundRoleChild) {
                            return {
                                id: foundRoleChild.id,
                                name: `${childEntityStore.name}:${foundRoleChild.name}`,
                                typeId: ModelType.Role,
                                roles: [],
                                labelsCount: 0
                            };
                        }
                    }
                }
            });

            return <CompositeEntity>{
                ...simpleEntity,
                children: demoralizedChildren,
                features: (<CompositeEntityStore>entityStore).featureStoreHouse.items
                    .map(d => getFeatureFromModel(d, entityStores, intentStores, phraseListStores))
                    .filter(Boolean)
            };
        case ModelType.List:
            return <ListEntity>{
                ...simpleEntity,
                sublists: (<ListEntityStore>entityStore).sublists.map(
                    sublist =>
                        <Sublist>{
                            id: sublist.id,
                            name: sublist.name,
                            values: sublist.values
                        }
                )
            };
        case ModelType.Regex:
            return <RegexEntity>{
                ...simpleEntity,
                pattern: (<RegexEntityStore>entityStore).pattern
            };

        default:
            return simpleEntity;
    }
};

/**
 * Denormalizes the features for a simple entity, converts constraint to a required feature.
 * @param entityStore Simple entity store to de-normalize.
 * @param entityStores All the available entity stores in the application.
 * @param intentStores All the available intent stores in the application.
 * @param phraseListStores All the available phraselist stores in the application.
 */
const deNormalizeFeatures = (
    entityStore: SimpleEntityStore,
    entityStores: EntityStore[],
    intentStores: IntentStore[],
    phraseListStores: PhraseListStore[]
) => {
    const features = entityStore.featureStoreHouse.items
        .map(d => getFeatureFromModel(d, entityStores, intentStores, phraseListStores))
        .filter(Boolean);

    if (entityStore.instanceOf && !features.find(f => f.name === entityStore.instanceOf)) {
        const store = entityStores.find(e => e.id === entityStore.instanceOf);
        features.push({ id: store.id, name: store.name, isRequired: true, typeId: 'entity' });
    }

    return features;
};

/**
 * Recursive function that de-normalize the N-Depth entity type and its children.
 *
 * @param entity The N-Depth entity to de-normalize
 * @param entities All the available entities in the application
 * @param intents All the available intents in the application
 * @param phraseLists All the available phrase lists in the application
 */
const deNormalizeNode = (
    entity: SimpleEntityStore,
    entities: EntityStore[],
    intents: IntentStore[],
    phraseLists: PhraseListStore[]
): SimpleEntity => {
    if (entity.instanceOf) {
        return {
            ...entity,
            children: [],
            roles: [],
            features: deNormalizeFeatures(entity, entities, intents, phraseLists),
            instanceOf: entitiesDenormalizer(
                /**
                 * @todo This comparison fixes the issue for not. The instanceOf
                 * de-normalization is broken, as we first updateInstanceOf in the
                 * store with the name, then de-normalize it to id, so autorun
                 * fires and throws an undefined error here because ids don't
                 * match names. Refer to omsour@microsoft.com for more details.
                 */
                [entities.find(e => e.id === entity.instanceOf || e.name === entity.instanceOf)],
                intents,
                phraseLists
            )[0]
        };
    }

    return {
        ...entity,
        instanceOf: undefined,
        roles: [],
        children: entity.children.map(c => deNormalizeNode(c, entities, intents, phraseLists)),
        features: entity.featureStoreHouse.items.map(d => getFeatureFromModel(d, entities, intents, phraseLists)).filter(Boolean)
    };
};

/**
 * De-normalize the feature store object to feature object
 *
 * @param featureStore The feature store object to map
 * @param entities All the available entities in the application
 * @param intents All the available intents in the application
 * @param phraseLists All the available phrase lists in the application
 */
const getFeatureFromModel = (
    featureStore: FeatureStore,
    entities: EntityStore[],
    intents: IntentStore[],
    phraseLists: PhraseListStore[]
): Feature => {
    const model =
        featureStore.typeId === 'feature'
            ? phraseLists.find(p => p.id === featureStore.id)
            : intents.find(i => i.id === featureStore.id) || entities.find(e => e.id === featureStore.id);
    if (!model) {
        return;
    }

    return {
        id: featureStore.id,
        name: model.name,
        typeId: featureStore.typeId === 'feature' ? 'feature' : (<EntityStore>model).typeId === ModelType.Intent ? 'intent' : 'entity',
        isRequired: featureStore.isRequired
    };
};

/**
 * De-normalize instanceOf to id instead of name
 *
 * @param simpleChildEntityStore The child to de-normalize
 * @param entityStores All the available entity stores
 */
export const instanceOfDeNormalizer = (simpleEntityStore: SimpleEntityStore, entityStores: EntityStore[]): void => {
    if (simpleEntityStore.instanceOf) {
        simpleEntityStore.updateInstanceOf(entityStores.find(e => e.name === simpleEntityStore.instanceOf).id);

        return;
    }

    simpleEntityStore.children.forEach(c => instanceOfDeNormalizer(c, entityStores));
};
